<script lang="ts" setup>
/* eslint-disable vue/no-v-html */

import type { SrcSetSizes } from '~/composables/useSrcSet'

export interface BannerProps {
    data: Banner
    headingLevel: string
}
const props = defineProps<BannerProps>()
const imageDesktopSrc = computed((): SrcSetSizes | undefined =>
    useSrcSetSizes(props.data.image?.path)
)
const imageMobileSrc = computed((): SrcSetSizes | undefined =>
    useSrcSetSizes(props.data?.image_mobile?.path || props.data?.image?.path)
)

const bannerBackgroundColor = computed((): { backgroundColor: string } => ({
    backgroundColor: props.data.background_color,
}))
</script>

<template>
  <aside
    class="rounded overflow-hidden"
    data-cy="banner"
  >
    <Tracker
      :data="{
        position: props.data.title,
        title: props.data.cta?.text.trim(),
      }"
    >
      <div class="row g-0">
        <div
          class="col-12 col-lg-5 text-center text-lg-start pt-6 px-4 p-lg-10 mb-5 mb-lg-0 position-relative"
          :style="bannerBackgroundColor"
        >
          <slot name="tag" />
          <Heading
            :level="props.headingLevel"
            class="mt-4 mt-lg-3 mb-lg-4 beta"
            :class="props.data.background_color === '#000000' && 'text-white'"
          >
            {{ props.data.title }}
          </Heading>
          <Typo v-if="props.data.description">
            <div
              class="fs-bigger fw-bolder mb-6 mx-auto"
              :class="props.data.background_color === '#000000' ? 'text-white' : 'text-black'"
              v-html="props.data.description"
            />
          </Typo>
          <div
            class="d-flex justify-content-center justify-content-lg-start"
          >
            <VButton
              v-if="props.data.cta?.text && props.data.cta?.link"
              :to="props.data.cta.link"
              :target="
                props.data.cta.isExternal ? '_blank' : '_self'
              "
              :label="props.data.cta.link"
              size="large"
              :color="props.data.cta.color"
              class="fs-small fs-lg-normal fw-bolder"
            >
              <span class="visually-hidden">Banner: {{ props.data.title }},</span>
              {{ props.data.cta.text }}
            </VButton>
          </div>

          <div
            class="banner-background z-index-5"
            :style="bannerBackgroundColor"
          />
        </div>
        <div class="col-12 col-lg-7 flex-shrink-0 z-index-10">
          <img
            v-if="imageMobileSrc?.srcSet"
            width="280"
            height="276"
            :srcset="imageMobileSrc?.srcSet"
            sizes="100vw"
            loading="lazy"
            decoding="sync"
            alt=""
            class="banner-image w-100 h-100 img-fluid d-lg-none"
          >
          <img
            v-if="imageDesktopSrc?.srcSet"
            width="750"
            height="300"
            :srcset="imageDesktopSrc?.srcSet"
            sizes="(min-width: 992px) 75vw, 69rem"
            loading="lazy"
            decoding="sync"
            alt=""
            class="banner-image h-100 d-none d-lg-block position-relative"
          >
        </div>
      </div>
    </Tracker>
  </aside>
</template>
